import { useState, useEffect } from 'react';
import { Briefcase, Search, Filter, Eye, Trash2, Flag, X, AlertTriangle, TrendingUp, CheckCircle, XCircle, MessageSquare, Users, Calendar, MapPin, DollarSign, Clock, ExternalLink } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { devLog, devError } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';

interface AdminGigsProps {
  accessToken: string;
}

interface GigStats {
  totalGigs: number;
  activeGigs: number;
  closedGigs: number;
  flaggedGigs: number;
  avgInterestsPerGig: number;
  conversionRate: number; // Interest → Conversation
}

interface AdminGig {
  id: string;
  title: string;
  description: string;
  location: string;
  salary: string;
  budget: string | null;
  estimatedDuration: string | null;
  workType: string;
  isActive: boolean;
  isFlagged: boolean;
  createdAt: string;
  userId: string;
  posterName: string;
  posterEmail: string;
  interestCount: number;
  conversationCount: number;
  viewCount: number; // If tracked
}

type FilterType = 'all' | 'active' | 'closed' | 'flagged';

export function AdminGigs({ accessToken }: AdminGigsProps) {
  const [stats, setStats] = useState<GigStats | null>(null);
  const [gigs, setGigs] = useState<AdminGig[]>([]);
  const [filteredGigs, setFilteredGigs] = useState<AdminGig[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [selectedGig, setSelectedGig] = useState<AdminGig | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchGigs();
  }, []);

  useEffect(() => {
    filterGigs();
  }, [gigs, searchQuery, filterType]);

  const fetchGigs = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/gigs`,
        {}
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
        setGigs(data.gigs);
        devLog('[ADMIN GIGS] Fetched:', data.gigs.length, 'gigs');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Kunne ikke hente oppdrag');
      }
    } catch (error) {
      devError('[ADMIN GIGS] Error fetching gigs:', error);
      toast.error('Noe gikk galt');
    } finally {
      setLoading(false);
    }
  };

  const filterGigs = () => {
    let filtered = [...gigs];

    // Filter by type
    if (filterType === 'active') {
      filtered = filtered.filter(g => g.isActive);
    } else if (filterType === 'closed') {
      filtered = filtered.filter(g => !g.isActive);
    } else if (filterType === 'flagged') {
      filtered = filtered.filter(g => g.isFlagged);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(g =>
        g.title.toLowerCase().includes(query) ||
        g.posterName.toLowerCase().includes(query) ||
        g.description.toLowerCase().includes(query)
      );
    }

    setFilteredGigs(filtered);
  };

  const handleDeleteGig = async (gigId: string) => {
    setDeleteLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/admin/gigs/${gigId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        toast.success('Oppdrag slettet');
        setGigs(gigs.filter(g => g.id !== gigId));
        setShowDeleteConfirm(false);
        setSelectedGig(null);
      } else {
        const error = await response.json();
        toast.error(error.error || 'Kunne ikke slette oppdrag');
      }
    } catch (error) {
      devError('[ADMIN GIGS] Error deleting gig:', error);
      toast.error('Noe gikk galt');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <Loader size="lg" className="text-purple-600" />
        <p className="mt-4 text-gray-600">Laster oppdrag...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-5 border-2 border-purple-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total oppdrag</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalGigs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-green-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Aktive</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeGigs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Lukkede</p>
                <p className="text-2xl font-bold text-gray-900">{stats.closedGigs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-red-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Flag className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Flaggede</p>
                <p className="text-2xl font-bold text-gray-900">{stats.flaggedGigs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-blue-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Gj.sn. interesser/oppdrag</p>
                <p className="text-2xl font-bold text-gray-900">{stats.avgInterestsPerGig.toFixed(1)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border-2 border-orange-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Konverteringsrate</p>
                <p className="text-2xl font-bold text-gray-900">{stats.conversionRate}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 border-2 border-gray-100 shadow-sm">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Søk etter tittel eller bedrift..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterType === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle
            </button>
            <button
              onClick={() => setFilterType('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterType === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Aktive
            </button>
            <button
              onClick={() => setFilterType('closed')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterType === 'closed'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Lukkede
            </button>
            <button
              onClick={() => setFilterType('flagged')}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filterType === 'flagged'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Flaggede
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-3">
          Viser {filteredGigs.length} av {gigs.length} oppdrag
        </p>
      </div>

      {/* Gigs List */}
      <div className="space-y-3">
        {filteredGigs.length === 0 ? (
          <div className="bg-white rounded-xl p-8 border-2 border-gray-100 text-center">
            <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Ingen oppdrag funnet</p>
          </div>
        ) : (
          filteredGigs.map((gig) => (
            <div
              key={gig.id}
              className="bg-white rounded-xl p-5 border-2 border-gray-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Left: Gig Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">{gig.title}</h3>
                        {gig.isFlagged && (
                          <Flag className="w-4 h-4 text-red-600" />
                        )}
                        {gig.isActive ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                            Aktiv
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                            Lukket
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        Publisert av: <span className="font-medium">{gig.posterName}</span> ({gig.posterEmail})
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                    {gig.description}
                  </p>

                  <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                    {gig.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {gig.location}
                      </div>
                    )}
                    {gig.salary && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        {gig.salary}
                      </div>
                    )}
                    {gig.estimatedDuration && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(gig.estimatedDuration).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(gig.createdAt).toLocaleDateString('nb-NO')}
                    </div>
                  </div>
                </div>

                {/* Right: Stats and Actions */}
                <div className="flex flex-col gap-3 lg:w-64">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-purple-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-gray-600">Interesser</p>
                      <p className="text-lg font-bold text-purple-600">{gig.interestCount}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-gray-600">Samtaler</p>
                      <p className="text-lg font-bold text-blue-600">{gig.conversationCount}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <p className="text-xs text-gray-600">Conv%</p>
                      <p className="text-lg font-bold text-green-600">
                        {gig.interestCount > 0
                          ? Math.round((gig.conversationCount / gig.interestCount) * 100)
                          : 0}%
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedGig(gig)}
                      className="flex-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                    >
                      <Eye className="w-4 h-4" />
                      Detaljer
                    </button>
                    <button
                      onClick={() => {
                        setSelectedGig(gig);
                        setShowDeleteConfirm(true);
                      }}
                      className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Gig Detail Modal */}
      {selectedGig && !showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedGig.title}</h2>
                <p className="text-sm text-gray-600">
                  Publisert av {selectedGig.posterName} • {new Date(selectedGig.createdAt).toLocaleDateString('nb-NO')}
                </p>
              </div>
              <button
                onClick={() => setSelectedGig(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="flex items-center gap-2">
                {selectedGig.isActive ? (
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Aktiv
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm font-medium rounded-full flex items-center gap-1">
                    <XCircle className="w-4 h-4" />
                    Lukket
                  </span>
                )}
                {selectedGig.isFlagged && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full flex items-center gap-1">
                    <Flag className="w-4 h-4" />
                    Flagget
                  </span>
                )}
              </div>

              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Beskrivelse</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedGig.description}</p>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Lokasjon</p>
                  <p className="text-gray-600">{selectedGig.location}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Arbeidsform</p>
                  <p className="text-gray-600">
                    {selectedGig.workType === 'remote' ? 'Fjernarbeid' : selectedGig.workType === 'onsite' ? 'På stedet' : 'Hybrid'}
                  </p>
                </div>
                {selectedGig.salary && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Lønn</p>
                    <p className="text-gray-600">{selectedGig.salary}</p>
                  </div>
                )}
                {selectedGig.budget && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Budsjett</p>
                    <p className="text-gray-600">{selectedGig.budget}</p>
                  </div>
                )}
                {selectedGig.estimatedDuration && (
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-1">Estimert varighet</p>
                    <p className="text-gray-600">{selectedGig.estimatedDuration}</p>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Statistikk</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Interesser</p>
                    <p className="text-2xl font-bold text-purple-600">{selectedGig.interestCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Samtaler</p>
                    <p className="text-2xl font-bold text-blue-600">{selectedGig.conversationCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Konverteringsrate</p>
                    <p className="text-2xl font-bold text-green-600">
                      {selectedGig.interestCount > 0
                        ? Math.round((selectedGig.conversationCount / selectedGig.interestCount) * 100)
                        : 0}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Poster Info */}
              <div className="bg-purple-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Publisert av</h3>
                <p className="text-gray-900 font-medium">{selectedGig.posterName}</p>
                <p className="text-gray-600 text-sm">{selectedGig.posterEmail}</p>
                <p className="text-gray-600 text-sm">Bruker-ID: {selectedGig.userId}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedGig && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Slett oppdrag?</h3>
                <p className="text-sm text-gray-600">
                  Er du sikker på at du vil slette "{selectedGig.title}"? Dette vil også slette alle interesser og samtaler knyttet til oppdraget.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedGig(null);
                }}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all disabled:opacity-50"
              >
                Avbryt
              </button>
              <button
                onClick={() => handleDeleteGig(selectedGig.id)}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleteLoading ? (
                  <>
                    <Loader size="sm" className="text-white" />
                    Sletter...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Slett
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
